import { useSelector } from "react-redux";
import type { ShopPageProps } from "@b2bportal/core-hotels";
import {
  HotelsRawEventName,
  HotelsShopSelectors,
  useEmitCoreHotelsRawEventOnce,
} from "@b2bportal/core-hotels";
import { HotelsAvailabilitySelectors } from "@b2bportal/core-hotels";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import { getAddressText } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  HOTELS_AVAILABILITY_PATH,
  HotelNavigationUtility,
} from "@overrides/utilities";
import {
  AboutHotelSection,
  CustomerReviewsSection,
  CheckInPoliciesSection,
  HotelInfoSection,
  ImageGallery,
  HotelRoomSelection,
  HotelDetailsSection,
  HotelShopBookingSection,
  HotelShopSearch,
} from "@tenant/hotels";
import { HotelShopMap } from "@tenant/hotels";
import { ErrorMessage, Icon, Loading, Page } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./ShopPage.module.scss";
import { NavigationTabs, useScrollSpy } from "@/components";
import { ActionButton } from "@/components/ActionButton";
import { useRefreshPoints } from "@/components/AuthModule/hooks";
import { SignInBanner } from "@/components/SignInBanner";
const COMPONENT_KEY = CoreHotelsComponent.ShopPage;
import { useMemo } from "react";
import { usePlaceFromLodgingSelection } from "@/utils/location/usePlaceFromLodgingSelection";
import { scrollToElement } from "@/utils/scroll";

export const ShopPage = ({
  className,
  children,
  context,
  text,
  handlers,
  ...pageProps
}: ShopPageProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();

  useRefreshPoints();

  const navigationTabs = [
    {
      label: t("core-hotels.shop.navigation.overview"),
      href: "#overview",
    },
    {
      label: t("core-hotels.shop.navigation.reviews"),
      href: "#reviews",
    },
    {
      label: t("core-hotels.shop.navigation.rooms"),
      href: "#rooms",
    },
    {
      label: t("core-hotels.shop.navigation.policies"),
      href: "#policies",
    },
  ];

  const activeHref = useScrollSpy(navigationTabs.map((tab) => tab.href));
  const dateRange = useSelector(HotelsShopSelectors.getShopSearch)?.dateRange;
  const guests = useSelector(HotelsAvailabilitySelectors.getSearchFormGuests);
  const place = usePlaceFromLodgingSelection();
  const roomsCount = useSelector(
    HotelsAvailabilitySelectors.getSearchFormRoomsCount
  );

  const availabilityPath = useMemo(() => {
    if (place != null && dateRange?.until != null && dateRange?.from != null) {
      const destinationCode = HotelNavigationUtility.placeLabelAndSublabelToUrl(
        place.label,
        place.subLabel
      );
      const searchParams = HotelNavigationUtility.buildSearchParams({
        dateRange: { from: dateRange.from, until: dateRange.until },
        guests,
        roomsCount,
      });

      return `${HOTELS_AVAILABILITY_PATH}/${destinationCode}?${searchParams.toString()}`;
    } else {
      return null;
    }
  }, [dateRange?.from, dateRange?.until, guests, place, roomsCount]);

  useEmitCoreHotelsRawEventOnce(
    {
      name: HotelsRawEventName.HotelShopPageLoaded,
    },
    !context.isLoading && context.hotel != null
  );

  if (context.isUnavailable || context.isError) {
    return (
      <Page className={clsx(block, className, cn("page-error"))}>
        <div className={cn("error")}>
          <ErrorMessage
            title={text.unavailableTitle}
            subtitle={t(
              "core-hotels.availabilityPage.results.noResults.noRoomsAvailable"
            )}
          />
          <ActionButton
            message={t("core-hotels.shop.selectAnotherHotel")}
            to={availabilityPath}
            target="_self"
          />
        </div>
      </Page>
    );
  }

  //TODO: skeleton loading on the components
  if (context.isLoading || context.hotel == null) {
    return (
      <Page
        title={text.title}
        className={clsx(block, className, cn("page-loading"))}
      >
        <div className={cn("loading")}>
          <Loading />
        </div>
      </Page>
    );
  }

  return (
    <Page title={text.title} className={block} {...pageProps}>
      <SignInBanner />
      <div className={cn("container")}>
        <ImageGallery
          className={cn("image-gallery")}
          media={context.hotel.lodging.media ?? []}
          name={context.hotel.lodging.name}
          onClickChooseRooms={() => scrollToElement("rooms")}
        />
        <div className={cn("first-row")}>
          <div className={cn("navigation-tabs")}>
            <NavigationTabs options={navigationTabs} activeHref={activeHref} />
          </div>

          <div className={cn("pricing")}>
            <HotelShopBookingSection
              hotel={context.hotel}
              goToNextStep={handlers.handleChooseRooms}
            />
          </div>
        </div>
        <div className={cn("content")}>
          <div className={cn("main-content")}>
            <div id="overview" className={cn("section", ["info"])}>
              <HotelInfoSection
                hotel={context.hotel}
                className={cn("hotel-info")}
              />
            </div>
            <div className={cn("section", ["mobile-pricing"])}>
              <HotelShopBookingSection
                hotel={context.hotel}
                goToNextStep={handlers.handleChooseRooms}
              />
            </div>
            <div className={cn("section", ["hotel-details"])}>
              <div className={cn("hotel-details")}>
                <div
                  className={clsx(
                    cn("hotel-location"),
                    cn("hotel-details-card")
                  )}
                >
                  <h2 className={cn("subsection-title")}>
                    {t("core-hotels.shop.location.title")}
                  </h2>
                  <div className={cn("location-address")}>
                    <Icon iconName={IconNameEnum.mapPin} />
                    {getAddressText(context.hotel.lodging.address)}
                  </div>
                  <HotelShopMap
                    hotel={context.hotel}
                    className={cn("hotel-map")}
                  />
                </div>
                <div className={clsx(cn("hotel-details-card"))}>
                  <HotelDetailsSection
                    amenities={context.amenities}
                    checkInInformation={context.checkInInformation}
                  />
                </div>
                <div className={cn("hotel-details-card")}>
                  <AboutHotelSection hotel={context.hotel} />
                </div>
              </div>
            </div>
          </div>
          {context.reviews != null ? (
            <div id="reviews" className={cn("section")}>
              <CustomerReviewsSection reviews={context.reviews} />
            </div>
          ) : null}
          <div id="rooms" className={cn("section", ["main", "rooms"])}>
            <h2 className={cn("choose-room-title")}>
              {text.roomSelectionTitle}
            </h2>
            <HotelShopSearch className={cn("shop-search")} />
            <HotelRoomSelection goToNextStep={handlers.goToNextStep} />
          </div>
          <div id="policies" className={cn("section", ["check-in-policies"])}>
            <CheckInPoliciesSection
              checkInPolicies={context.checkInPolicies}
              className={cn("check-in-policies")}
            />
          </div>

          {children}
        </div>
      </div>
    </Page>
  );
};
